import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { assignUnitFields } from "./storageFields";

interface AssignUnitModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { customerName: string; assignedDate: string; notes?: string }) => void;
  unitRefNo?: string;
}

const AssignUnitModal: React.FC<AssignUnitModalProps> = ({
  open,
  onClose,
  onSubmit,
  unitRefNo,
}) => {
  const [formData, setFormData] = useState<any>({
    customerName: "",
    assignedDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [errors, setErrors] = useState<any>({});

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev: any) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validate = () => {
    const newErrors: any = {};
    assignUnitFields.forEach((field) => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      customerName: "",
      assignedDate: new Date().toISOString().split("T")[0],
      notes: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: "18px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        Assign Unit {unitRefNo ? `- ${unitRefNo}` : ""}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {assignUnitFields.map((field) => {
            if (field.type === "textarea") {
              return (
                <TextField
                  key={field.id}
                  fullWidth
                  multiline
                  rows={field.rows || 3}
                  label={field.label}
                  placeholder={field.placeholder}
                  value={formData[field.id]}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  required={field.required}
                  error={!!errors[field.id]}
                  helperText={errors[field.id]}
                  size="small"
                />
              );
            }

            return (
              <TextField
                key={field.id}
                fullWidth
                type={field.type}
                label={field.label}
                placeholder={field.placeholder}
                value={formData[field.id]}
                onChange={(e) => handleChange(field.id, e.target.value)}
                required={field.required}
                error={!!errors[field.id]}
                helperText={errors[field.id]}
                size="small"
                InputLabelProps={
                  field.type === "date" ? { shrink: true } : undefined
                }
              />
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#666",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: "#1B7B67",
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#155f51",
            },
          }}
        >
          Assign Unit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUnitModal;
